import { InputNumber, type InputNumberProps } from 'antd';
import dayjs from 'dayjs';

/**
 * The UNIQUE place display formatting happens in this app. Every value
 * arrives over the wire exactly as the API sends it — decimal strings for
 * money/quantities, ISO strings for dates (see task brief §9: "conversiile
 * de afișare DOAR în lib/format"). Converting through `Number()` here is
 * fine — it's for display only, never fed back into arithmetic — but it
 * must never happen anywhere else in the app.
 */

const moneyFormatter = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('ro-RO');

const qtyFormatter = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

export function formatMoney(value: string, currency = 'RON'): string {
  return `${moneyFormatter.format(Number(value))} ${currency}`;
}

export function formatNumber(value: string | number): string {
  return numberFormatter.format(Number(value));
}

/** Up to 3dp, trailing zeros trimmed ("2.500" -> "2,5", "3.000" -> "3"). */
export function formatQty(value: string): string {
  return qtyFormatter.format(Number(value));
}

export function formatDate(iso: string): string {
  return dayjs(iso).format('DD.MM.YYYY');
}

export function MoneyText({
  value,
  currency = 'RON',
  className,
}: {
  value: string;
  currency?: string;
  className?: string;
}) {
  return <span className={className ? `num ${className}` : 'num'}>{formatMoney(value, currency)}</span>;
}

export function DateText({ value }: { value: string | null | undefined }) {
  return <span>{value ? formatDate(value) : '—'}</span>;
}

// AntD's InputNumber<string> in `stringMode` never round-trips the value
// through a JS `number` — onChange hands back the same kind of decimal
// string the API expects on the way in, which is the whole point (a single
// float64 round-trip is enough to corrupt a money value, see money.ts).
type DecimalInputProps = Omit<InputNumberProps<string>, 'stringMode' | 'decimalSeparator' | 'precision'>;

export function MoneyInput(props: DecimalInputProps) {
  return <InputNumber<string> stringMode precision={2} decimalSeparator="," style={{ width: '100%' }} {...props} />;
}

export function QtyInput(props: DecimalInputProps) {
  return <InputNumber<string> stringMode precision={3} decimalSeparator="," style={{ width: '100%' }} {...props} />;
}
